import { useCallback, useLayoutEffect, useRef, useState } from 'react'

const SCROLL_PIN_THRESHOLD_PX = 80

interface PinController {
  isPinnedToBottom: boolean
  onScroll: () => void
  scrollToBottom: () => void
}

/**
 * Keeps `scrollRef` glued to the bottom while new content streams in.
 *
 * Two layered effects handle two different growth modes:
 *  - Streaming text: every message_delta re-renders the bubble (effect A).
 *  - Async children that mount after first paint, e.g. charts and tables
 *    whose layout settles over hundreds of ms (effect B).
 *
 * Disabled once the user has scrolled away from the bottom.
 *
 * @param isLoading true while a session's messages are being fetched.
 *   Effect B runs once `isLoading` transitions back to false, which marks
 *   "fresh content just landed, set up a new observation window."
 */
export function useScrollPinnedToBottom(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  contentRef: React.RefObject<HTMLDivElement | null>,
  isLoading: boolean,
): PinController {
  const isPinnedRef = useRef(true)
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true)

  const setPinned = useCallback((pinned: boolean) => {
    isPinnedRef.current = pinned
    setIsPinnedToBottom(pinned)
  }, [])

  // Effect A — per-render pin during streaming. Each message_delta re-renders
  // ChatThread, which fires this. Doesn't depend on refs being attached when
  // the ResizeObserver in effect B was set up (a problem on brand-new
  // sessions where the empty state renders before the thread mounts).
  useLayoutEffect(() => {
    if (!isPinnedRef.current) return
    const container = scrollRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
  })

  // Effect B — re-establishes a pin window when fresh content loads. The
  // cascade of timers + rAFs is load-bearing: async children (charts, tables)
  // settle at different beats and no single strategy catches every case.
  // ResizeObserver picks up the long tail after the timers stop firing.
  useLayoutEffect(() => {
    if (isLoading) return
    const container = scrollRef.current
    const content = contentRef.current
    if (!container || !content) return

    setPinned(true)
    container.scrollTop = container.scrollHeight

    let cancelled = false
    function pinNow() {
      if (cancelled) return
      const target = scrollRef.current
      if (!target) return
      target.scrollTop = target.scrollHeight
    }

    pinNow()
    const raf = requestAnimationFrame(() => {
      pinNow()
      requestAnimationFrame(pinNow)
    })
    const timers = [
      setTimeout(pinNow, 50),
      setTimeout(pinNow, 150),
      setTimeout(pinNow, 400),
      setTimeout(pinNow, 900),
    ]

    const observer = new ResizeObserver(() => {
      if (!isPinnedRef.current) return
      const target = scrollRef.current
      if (!target) return
      target.scrollTop = target.scrollHeight
    })
    observer.observe(content)

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      timers.forEach((id) => clearTimeout(id))
      observer.disconnect()
    }
  }, [isLoading, scrollRef, contentRef, setPinned])

  const onScroll = useCallback(() => {
    const container = scrollRef.current
    if (!container) return
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight
    setPinned(distanceFromBottom < SCROLL_PIN_THRESHOLD_PX)
  }, [scrollRef, setPinned])

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current
    if (!container) return
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    setPinned(true)
  }, [scrollRef, setPinned])

  return { isPinnedToBottom, onScroll, scrollToBottom }
}
